import React, { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Paper,
  Stack,
  Tabs,
  Tab,
  Avatar
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  KeyboardArrowUp as UpIcon,
  KeyboardArrowDown as DownIcon,
  Link as LinkIcon,
  CloudUpload as UploadIcon
} from "@mui/icons-material";
import { MediaUploader } from "./MediaUploader";

const MediaPreview = ({ url, size = 48 }) => {
  const [hasError, setHasError] = useState(false);

  if (hasError || !url) {
    return (
      <Avatar
        sx={{
          width: size,
          height: size,
          bgcolor: "grey.300",
          fontSize: "12px"
        }}
      >
        IMG
      </Avatar>
    );
  }

  return (
    <Avatar
      src={url}
      sx={{
        width: size,
        height: size,
        borderRadius: 1
      }}
      onError={() => setHasError(true)}
      variant="rounded"
    />
  );
};

export const MediaManager = ({ media = [], onChange }) => {
  const [newMediaUrl, setNewMediaUrl] = useState("");
  const [newMediaType, setNewMediaType] = useState("image");
  const [activeTab, setActiveTab] = useState(0);

  const handleAddMedia = () => {
    if (!newMediaUrl.trim()) { return; }

    const newMedia = {
      url: newMediaUrl.trim(),
      type: newMediaType,
      order: media.length
    };

    onChange([...media, newMedia]);
    setNewMediaUrl("");
    setNewMediaType("image");
  };

  const handleUploadSuccess = (uploadedMedia) => {
    const newMedia = {
      ...uploadedMedia,
      order: media.length
    };
    onChange([...media, newMedia]);
  };

  const handleDeleteMedia = (index) => {
    const updatedMedia = media.filter((_, i) => i !== index);
    const reorderedMedia = updatedMedia.map((item, i) => ({
      ...item,
      order: i
    }));
    onChange(reorderedMedia);
  };

  const handleMoveUp = (index) => {
    if (index === 0) { return; }

    const updatedMedia = [...media];
    [updatedMedia[index - 1], updatedMedia[index]] = [
      updatedMedia[index],
      updatedMedia[index - 1]
    ];

    const reorderedMedia = updatedMedia.map((item, i) => ({
      ...item,
      order: i
    }));
    onChange(reorderedMedia);
  };

  const handleMoveDown = (index) => {
    if (index === media.length - 1) { return; }

    const updatedMedia = [...media];
    [updatedMedia[index], updatedMedia[index + 1]] = [
      updatedMedia[index + 1],
      updatedMedia[index]
    ];

    const reorderedMedia = updatedMedia.map((item, i) => ({
      ...item,
      order: i
    }));
    onChange(reorderedMedia);
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Product Media
      </Typography>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)} sx={{ mb: 2 }}>
          <Tab icon={<UploadIcon />} label="Upload Files" />
          <Tab icon={<LinkIcon />} label="Add URL" />
        </Tabs>

        {activeTab === 0 && (
          <MediaUploader onUploadSuccess={handleUploadSuccess} />
        )}

        {activeTab === 1 && (
          <Stack direction="row" spacing={1} alignItems="end">
            <TextField
              label="Media URL"
              value={newMediaUrl}
              onChange={(e) => setNewMediaUrl(e.target.value)}
              fullWidth
              size="small"
              onKeyPress={(e) => {
                if (e.key === "Enter") {
                  handleAddMedia();
                }
              }}
            />
            <Button
              variant="contained"
              onClick={handleAddMedia}
              startIcon={<AddIcon />}
              disabled={!newMediaUrl.trim()}
            >
              Add
            </Button>
          </Stack>
        )}
      </Paper>

      {media.length > 0 && (
        <Box>
          {media.map((mediaItem, index) => (
            <Paper key={index} sx={{ p: 2, mb: 1 }}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <MediaPreview url={mediaItem.url} />
                <Typography variant="body2" sx={{ minWidth: 30 }}>
                  {index + 1}.
                </Typography>
                <TextField
                  value={mediaItem.url}
                  onChange={(e) => {
                    const updatedMedia = [...media];
                    updatedMedia[index] = {
                      ...updatedMedia[index],
                      url: e.target.value
                    };
                    onChange(updatedMedia);
                  }}
                  size="small"
                  fullWidth
                  placeholder="Enter image URL..."
                />
                <Stack direction="row" spacing={0.5}>
                  <IconButton
                    size="small"
                    onClick={() => handleMoveUp(index)}
                    disabled={index === 0}
                    title="Move up"
                  >
                    <UpIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleMoveDown(index)}
                    disabled={index === media.length - 1}
                    title="Move down"
                  >
                    <DownIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleDeleteMedia(index)}
                    color="error"
                    title="Delete"
                  >
                    <DeleteIcon />
                  </IconButton>
                </Stack>
              </Stack>
            </Paper>
          ))}
        </Box>
      )}

      {media.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          No media added yet. Use the form above to add product images or videos.
        </Typography>
      )}
    </Box>
  );
};
